import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Plus, Search, Filter, Download, Lock, AlertTriangle, Bell, Clock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import DocumentModal from "@/components/documents/DocumentModal";
import DocumentUploadModal from "@/components/documents/DocumentUploadModal";
import WrittenDocEditor from "@/components/documents/WrittenDocEditor";
import { WrittenDocsSection } from "@/components/documents/WrittenDocsSection";
import { toast } from "sonner";

const STATUS_TONE = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300",
  "Pending Approval": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300",
  "Under Review": "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300",
  Superseded: "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const TYPE_TONE = {
  Policy: "from-indigo-500 to-blue-500",
  Procedure: "from-blue-500 to-cyan-500",
  "Work Instruction": "from-emerald-500 to-teal-500",
  Form: "from-amber-500 to-orange-500",
  Manual: "from-purple-500 to-fuchsia-500",
  Register: "from-rose-500 to-pink-500",
  External: "from-slate-500 to-slate-700",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function DocumentsPage() {
  const [params, setParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [authorized, setAuthorized] = useState(null);
  const [q, setQ] = useState("");
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [docType, setDocType] = useState("all");
  const [status, setStatus] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [written, setWritten] = useState([]);
  const [editor, setEditor] = useState({ open: false, id: null });

  const loadWritten = () => endpoints.listWrittenDocs(clientSlug !== "all" ? { client_slug: clientSlug } : {}).then(setWritten).catch(() => {});

  const load = () => {
    const p = {};
    if (clientSlug !== "all") p.client_slug = clientSlug;
    if (docType !== "all") p.doc_type = docType;
    if (status !== "all") p.status = status;
    return endpoints.listDocuments(p).then(setDocs).catch(() => {});
  };

  const loadAlerts = () => {
    const p = { days: 30 };
    if (clientSlug !== "all") p.client_slug = clientSlug;
    return endpoints.documentAlerts(p).then((a) => setAlerts(a.alerts || [])).catch(() => {});
  };

  useEffect(() => {
    endpoints.me().then((u) => {
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(!!me?.is_manager || true); // documents visible to all staff
      });
    });
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);

  useEffect(() => {
    if (authorized !== null) { load(); loadAlerts(); loadWritten(); }
    const next = new URLSearchParams(params);
    if (clientSlug === "all") next.delete("client"); else next.set("client", clientSlug);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, clientSlug, docType, status]);

  const filtered = useMemo(() => docs.filter((d) => {
    if (q && !`${d.doc_number} ${d.title} ${d.owner} ${d.client_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [docs, q]);

  const stats = useMemo(() => {
    const s = { total: docs.length, approved: 0, pending: 0, review: 0, overdue: 0 };
    docs.forEach((d) => {
      if (d.status === "Approved") s.approved++;
      if (d.status === "Pending Approval") s.pending++;
      if (d.review_due) s.review++;
      if (d.review_overdue) s.overdue++;
    });
    return s;
  }, [docs]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;

  const csvHref = endpoints.exportDocumentsCsvUrl(clientSlug !== "all" ? { client_slug: clientSlug } : {});

  const StatCard = ({ label, value, tone, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md card-hover transition-all ${active ? "ring-2 ring-indigo-500/50" : ""} border-slate-200/70 dark:border-white/10`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
    </button>
  );

  const openCurrent = (d) => {
    if (d.status !== "Approved" || !d.current_revision_id) {
      toast.error("No approved revision to open yet");
      return;
    }
    window.open(endpoints.documentCurrentUrl(d.id), "_blank");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/25">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Controlled Documents & Procedures</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {docs.length} controlled documents · revision history preserved</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            data-testid="doc-alerts-button"
            variant={showAlerts ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAlerts((v) => !v)}
            className={showAlerts ? "brand-gradient text-white" : ""}
          >
            <Bell className="h-3.5 w-3.5 mr-1" /> Review alerts
            {alerts.length > 0 && (
              <Badge className="ml-1.5 bg-rose-500 text-white border-0 rounded-full text-[10px] px-1.5 py-0">{alerts.length}</Badge>
            )}
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="doc-export-csv-button">
            <a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a>
          </Button>
          <Button data-testid="doc-new-button" className="brand-gradient text-white" size="sm" onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New document
          </Button>
        </div>
      </div>

      {/* Alerts panel */}
      {showAlerts && (
        <div data-testid="doc-alerts-panel" className="glass-card rounded-3xl p-4 mb-5 border-l-4 border-amber-400">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h3 className="font-display font-bold text-[14px]">Documents needing review ({alerts.length})</h3>
          </div>
          {alerts.length === 0 ? (
            <p className="text-xs text-slate-500">No documents due for review in the next 30 days.</p>
          ) : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="rounded-xl border border-amber-200 bg-amber-50/60 dark:bg-amber-500/10 p-3 flex items-center gap-3">
                  <Clock className={`h-4 w-4 ${a.status === "Overdue" ? "text-rose-600" : "text-amber-600"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      <span className="font-mono text-xs mr-1">{a.doc_number}</span>{a.title}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {a.client_name} · Owner {a.owner || "—"} · Next review {fmtDate(a.next_review_date)}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] rounded-full ${a.status === "Overdue" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {a.status}
                  </Badge>
                  <Button size="sm" variant="outline" data-testid={`doc-alert-open-${a.id}`} onClick={() => setModal({ open: true, id: a.id })}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Total" value={stats.total} tone="text-indigo-600" testid="doc-stat-total" onClick={() => setStatus("all")} active={status === "all"} />
        <StatCard label="Approved" value={stats.approved} tone="text-emerald-600" testid="doc-stat-approved" onClick={() => setStatus("Approved")} active={status === "Approved"} />
        <StatCard label="Pending" value={stats.pending} tone="text-amber-600" testid="doc-stat-pending" onClick={() => setStatus("Pending Approval")} active={status === "Pending Approval"} />
        <StatCard label="Review due" value={stats.review} tone="text-amber-600" testid="doc-stat-review" onClick={() => setShowAlerts(true)} />
        <StatCard label="Overdue" value={stats.overdue} tone="text-rose-600" testid="doc-stat-overdue" onClick={() => setShowAlerts(true)} />
      </div>

      {/* Filters */}
      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="doc-search-input" placeholder="Search doc #, title, owner..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={clientSlug} onValueChange={setClientSlug}>
          <SelectTrigger data-testid="doc-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger data-testid="doc-type-filter" className="h-9 w-[180px] text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.keys(TYPE_TONE).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Register */}
      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No documents match your filters.</div>
      ) : (
        <div className="glass-card rounded-3xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Doc #</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-center px-4 py-3">Rev</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Effective</th>
                <th className="text-left px-4 py-3">Next review</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`doc-row-${d.id}`}
                  className={`border-t border-slate-100 dark:border-white/5 hover:bg-slate-50/60 dark:hover:bg-white/5 ${d.review_overdue ? "bg-rose-50/30" : d.review_due ? "bg-amber-50/30" : ""}`}
                >
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{d.doc_number}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`doc-open-current-${d.id}`}
                      onClick={() => openCurrent(d)}
                      className="text-left hover:text-indigo-600 flex items-center gap-1 group"
                      title="Open current approved version"
                    >
                      <span className="font-medium">{d.title}</span>
                      {d.status === "Approved" && <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </button>
                    <div className="text-[10px] text-slate-500">{d.client_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full text-white bg-gradient-to-r ${TYPE_TONE[d.doc_type] || "from-slate-500 to-slate-600"}`}>
                      {d.doc_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs font-semibold">{d.current_revision || "—"}</td>
                  <td className="px-4 py-3 text-xs">{d.owner || "—"}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(d.current_effective_date)}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      {d.review_overdue && <AlertTriangle className="h-3 w-3 text-rose-500" />}
                      {d.review_due && !d.review_overdue && <Clock className="h-3 w-3 text-amber-500" />}
                      <span className={d.review_overdue ? "text-rose-600 font-semibold" : d.review_due ? "text-amber-600 font-semibold" : ""}>
                        {fmtDate(d.current_next_review_date)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[d.status] || ""}`}>{d.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`doc-details-${d.id}`} onClick={() => setModal({ open: true, id: d.id })}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WrittenDocsSection
        docs={written.filter((d) => !q || `${d.doc_number} ${d.title} ${d.authorised_by} ${d.client_name}`.toLowerCase().includes(q.toLowerCase()))}
        onNew={() => setEditor({ open: true, id: null })}
        onOpen={(id) => setEditor({ open: true, id })}
      />

      <WrittenDocEditor
        open={editor.open}
        docId={editor.id}
        clients={clients}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        onOpenChange={(o) => { if (!o) setEditor({ open: false, id: null }); }}
        onSaved={loadWritten}
      />

      <DocumentUploadModal
        open={uploadOpen}
        clients={clients}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        onOpenChange={setUploadOpen}
        onCreated={() => { setUploadOpen(false); load(); loadAlerts(); }}
      />

      <DocumentModal
        open={modal.open}
        documentId={modal.id}
        onOpenChange={(o) => { if (!o) setModal({ open: false, id: null }); }}
        onChanged={() => { load(); loadAlerts(); }}
      />
    </div>
  );
}
